import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTickets } from 'contexts/TicketsContext';
import { RoutePathsE } from 'declarations/enums/routes';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';

import type { NavTabItemT } from './MainHeader';
import { MainHeader } from './MainHeader';

/**
 * Wrapper component that provides TicketsContext to MainHeader.
 * Renders nav tabs (Tickets | Schedule | Insights) inline in header like WarrInsights.
 * Ticket tabs (home + opened tickets) are section-scoped: schedule tabs only show when
 * Schedule is selected; normal ticket tabs only show when Tickets is selected.
 */
export const MainHeaderWithTabs: FC = () => {
  const ticketsContext = useTickets();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    activeTab,
    setActiveTab,
    setOpenedScheduleTicketTab,
    setIsScheduleTicketList,
    openedTicketTabs,
    openedScheduleTicketTabs,
    closeTicketTab,
    closeScheduleTicketTab,
  } = ticketsContext;

  const isViewingTicketDetail = location.pathname.startsWith(`${RoutePathsE.TICKET_DETAIL}/`);
  const currentTicketId = isViewingTicketDetail
    ? location.pathname.replace(`${RoutePathsE.TICKET_DETAIL}/`, '')
    : null;

  const effectiveSection = useMemo((): ActiveTabTypeE => {
    if (currentTicketId) {
      const params = new URLSearchParams(location.search);
      if (params.get('section') === 'schedule') return ActiveTabTypeE.Schedule;
    }
    return activeTab;
  }, [currentTicketId, location.search, activeTab]);

  const currentOpenedTabs =
    effectiveSection === ActiveTabTypeE.Schedule ? openedScheduleTicketTabs : openedTicketTabs;

  useEffect(() => {
    if (currentTicketId && effectiveSection !== activeTab) {
      setActiveTab(effectiveSection);
      sessionStorage.setItem('activeTab', effectiveSection);
      // Do not set isScheduleTicketList here - it must stay as-is so returning to list shows correct view (cards vs list)
    }
  }, [currentTicketId, effectiveSection, activeTab, setActiveTab]);

  /** Apply section=schedule from URL when opening a schedule ticket - runs before effectiveSection so we stay in Schedule */
  useEffect(() => {
    if (!currentTicketId) return;
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    const tabLabel = params.get('tabLabel');
    if (section !== 'schedule' || !tabLabel) return;
    if (Object.values(openedScheduleTicketTabs).includes(currentTicketId)) return;

    setOpenedScheduleTicketTab(tabLabel, currentTicketId);
    setActiveTab(ActiveTabTypeE.Schedule);
    sessionStorage.setItem('activeTab', ActiveTabTypeE.Schedule);
    // Do not set isScheduleTicketList - preserve so return shows correct view (cards vs list)

    params.delete('section');
    params.delete('tabLabel');
    const newSearch = params.toString() ? `?${params.toString()}` : '';
    navigate(`${location.pathname}${newSearch}`, { replace: true });
  }, [
    currentTicketId,
    location.search,
    location.pathname,
    openedScheduleTicketTabs,
    setOpenedScheduleTicketTab,
    setActiveTab,
    navigate,
  ]);

  const ticketTabIndex = useMemo((): number => {
    const path = location.pathname;
    if (!path.startsWith(`${RoutePathsE.TICKET_DETAIL}/`)) return 0;
    const ticketId = path.replace(`${RoutePathsE.TICKET_DETAIL}/`, '');
    const ids = Object.values(currentOpenedTabs);
    const idx = ids.indexOf(ticketId);
    return idx === -1 ? 0 : idx + 1;
  }, [location.pathname, currentOpenedTabs]);

  const handleTicketTabClick = useCallback(
    (ticketId: string, route: string): void => {
      const cleanSearch = new URLSearchParams(location.search);
      cleanSearch.delete('section');
      cleanSearch.delete('tabLabel');
      const searchStr = cleanSearch.toString();
      const targetPath = searchStr ? `${route}?${searchStr}` : route;
      if (ticketId === '' && route === RoutePathsE.TICKETS_PAGE) {
        if (effectiveSection === ActiveTabTypeE.Schedule) {
          setActiveTab(ActiveTabTypeE.Schedule);
          sessionStorage.setItem('activeTab', ActiveTabTypeE.Schedule);
          const hasScheduleFileInUrl =
            cleanSearch.get(TicketsRefinementKeysE.ScheduleFileId) != null;
          setIsScheduleTicketList(hasScheduleFileInUrl);
        } else {
          setActiveTab(ActiveTabTypeE.Tickets);
          sessionStorage.setItem('activeTab', ActiveTabTypeE.Tickets);
          setIsScheduleTicketList(false);
        }
        navigate(targetPath);
        return;
      }
      navigate(targetPath);
    },
    [effectiveSection, location.search, setActiveTab, setIsScheduleTicketList, navigate]
  );

  const handleCloseTicketTab = useCallback(
    (e: React.MouseEvent, tabLabel: string, ticketId: string): void => {
      e.preventDefault();
      e.stopPropagation();
      const path = location.pathname;
      const isOnTicketDetail = path.startsWith(`${RoutePathsE.TICKET_DETAIL}/`);
      const isClosingActiveTab = currentOpenedTabs[tabLabel] === ticketId;
      if (effectiveSection === ActiveTabTypeE.Schedule) {
        closeScheduleTicketTab(tabLabel);
      } else {
        closeTicketTab(tabLabel);
      }
      if (isOnTicketDetail && isClosingActiveTab) {
        const cleanSearch = new URLSearchParams(location.search);
        cleanSearch.delete('section');
        cleanSearch.delete('tabLabel');
        const searchStr = cleanSearch.toString();
        navigate({ pathname: RoutePathsE.TICKETS_PAGE, search: searchStr ? `?${searchStr}` : '' });
      }
    },
    [
      location.pathname,
      location.search,
      currentOpenedTabs,
      effectiveSection,
      closeTicketTab,
      closeScheduleTicketTab,
      navigate,
    ]
  );

  const handleTabChange = useCallback(
    (tab: ActiveTabTypeE): void => {
      ticketsContext.setActiveTab(tab);
      sessionStorage.setItem('activeTab', tab);
      if (tab === ActiveTabTypeE.Schedule) {
        setIsScheduleTicketList(false);
        ticketsContext.updateSearchParamsArray(TicketsRefinementKeysE.Category, null);
      } else {
        setIsScheduleTicketList(false);
        ticketsContext.updateSearchParamsArray(TicketsRefinementKeysE.Category, null);
        ticketsContext.updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
        ticketsContext.updateSearchParamsArray(TicketsRefinementKeysE.Schedule, null);
      }
      if (isViewingTicketDetail) {
        const cleanSearch = new URLSearchParams(location.search);
        cleanSearch.delete('section');
        cleanSearch.delete('tabLabel');
        const searchStr = cleanSearch.toString();
        navigate({ pathname: RoutePathsE.TICKETS_PAGE, search: searchStr ? `?${searchStr}` : '' });
      }
    },
    [ticketsContext, setIsScheduleTicketList, isViewingTicketDetail, location.search, navigate]
  );

  const navTabs: NavTabItemT[] = useMemo(
    () => [
      {
        label: 'Tickets',
        isActive: effectiveSection === ActiveTabTypeE.Tickets,
        onClick: () => handleTabChange(ActiveTabTypeE.Tickets),
      },
      {
        label: 'Schedule',
        isActive: effectiveSection === ActiveTabTypeE.Schedule,
        onClick: () => handleTabChange(ActiveTabTypeE.Schedule),
      },
      {
        label: 'Insights',
        isActive: effectiveSection === ActiveTabTypeE.Insights,
        onClick: () => handleTabChange(ActiveTabTypeE.Insights),
      },
    ],
    [effectiveSection, handleTabChange]
  );

  const showTicketTabsRow =
    (effectiveSection === ActiveTabTypeE.Tickets || effectiveSection === ActiveTabTypeE.Schedule) &&
    Object.keys(currentOpenedTabs).length > 0;

  return (
    <MainHeader
      navTabs={navTabs}
      openedTicketTabs={showTicketTabsRow ? currentOpenedTabs : {}}
      ticketTabIndex={ticketTabIndex}
      onTicketTabClick={showTicketTabsRow ? handleTicketTabClick : undefined}
      onCloseTicketTab={showTicketTabsRow ? handleCloseTicketTab : undefined}
    />
  );
};
